import { envConfig } from '@/lib/env-config';
import { IntegrationApiClient } from '@/lib/integration-api-client';
import { IntegrationTokenManager } from '@/lib/integration-token-manager';
import { useCallback } from 'react';
import { useIntegration } from './use-integration';
import { useIntegrationToken } from './use-integration-token';

// Type for API response data
type ApiResponseData = Record<string, unknown>;

// Type for request data
type RequestData = Record<string, unknown>;

/**
 * Hook for calling integration partner endpoints directly
 * Provides a simple interface to call any endpoint by action and environment
 */
export const useIntegrationPartnerEndpoint = () => {
  const { isValid: hasValidToken, token } = useIntegrationToken();
  const { endpoints, partner } = useIntegration();

  /**
   * Call an integration partner endpoint by action and environment
   */
  const callEndpoint = useCallback(
    async (
      action: string,
      data?: RequestData,
      options?: RequestInit,
      environment: string = envConfig.env
    ): Promise<{
      success: boolean;
      data?: ApiResponseData;
      error?: string;
    }> => {
      // Check if we have a valid token
      if (!hasValidToken) {
        console.error('❌ No valid integration token available');
        console.error('🔍 Debug info:', {
          hasValidToken,
          hasToken: !!token,
          tokenInfo: IntegrationTokenManager.getTokenInfo(),
        });
        return {
          success: false,
          error:
            'No valid integration token available. Please authenticate first.',
        };
      }

      // Check if we have endpoints
      if (endpoints.length === 0) {
        console.error('❌ No integration endpoints available');
        return {
          success: false,
          error: 'No integration endpoints available.',
        };
      }

      // Check if we have a partner
      if (!partner) {
        console.error('❌ No integration partner available');
        return {
          success: false,
          error: 'No integration partner available.',
        };
      }

      // Use the IntegrationApiClient to make the call
      return IntegrationApiClient.callEndpointByAction(
        action,
        data,
        options,
        environment
      );
    },
    [hasValidToken, endpoints.length, partner, token]
  );

  /**
   * Make a GET request to an endpoint
   */
  const get = useCallback(
    async (
      action: string,
      options?: RequestInit,
      environment: 'development' | 'staging' | 'production' = 'production'
    ) => {
      return callEndpoint(
        action,
        undefined,
        { ...options, method: 'GET' },
        environment
      );
    },
    [callEndpoint]
  );

  /**
   * Make a POST request to an endpoint
   */
  const post = useCallback(
    async (
      action: string,
      data?: RequestData,
      options?: RequestInit,
      environment: 'development' | 'staging' | 'production' = 'production'
    ) => {
      return callEndpoint(
        action,
        data,
        { ...options, method: 'POST' },
        environment
      );
    },
    [callEndpoint]
  );

  /**
   * Make a PUT request to an endpoint
   */
  const put = useCallback(
    async (
      action: string,
      data?: RequestData,
      options?: RequestInit,
      environment: 'development' | 'staging' | 'production' = 'production'
    ) => {
      return callEndpoint(
        action,
        data,
        { ...options, method: 'PUT' },
        environment
      );
    },
    [callEndpoint]
  );

  /**
   * Make a DELETE request to an endpoint
   */
  const del = useCallback(
    async (
      action: string,
      options?: RequestInit,
      environment: 'development' | 'staging' | 'production' = 'production'
    ) => {
      return callEndpoint(
        action,
        undefined,
        { ...options, method: 'DELETE' },
        environment
      );
    },
    [callEndpoint]
  );

  /**
   * Make a PATCH request to an endpoint
   */
  const patch = useCallback(
    async (
      action: string,
      data?: RequestData,
      options?: RequestInit,
      environment: 'development' | 'staging' | 'production' = 'production'
    ) => {
      return callEndpoint(
        action,
        data,
        { ...options, method: 'PATCH' },
        environment
      );
    },
    [callEndpoint]
  );

  /**
   * Check if we can make API calls
   */
  const canMakeApiCalls = useCallback(() => {
    return IntegrationApiClient.canMakeApiCalls();
  }, []);

  /**
   * Get available endpoints for a specific action
   */
  const getEndpointsForAction = useCallback((action: string) => {
    return IntegrationApiClient.getEndpointsForAction(action);
  }, []);

  /**
   * Get available endpoints for a specific environment
   */
  const getEndpointsForEnvironment = useCallback(
    (environment: 'development' | 'staging' | 'production') => {
      return IntegrationApiClient.getEndpointsForEnvironment(environment);
    },
    []
  );

  /**
   * Get integration status
   */
  const getStatus = useCallback(() => {
    return IntegrationApiClient.getStatus();
  }, []);

  return {
    // HTTP method functions
    callEndpoint,
    get,
    post,
    put,
    delete: del,
    patch,

    // Utility functions
    canMakeApiCalls,
    getEndpointsForAction,
    getEndpointsForEnvironment,
    getStatus,

    // State
    hasValidToken,
    hasEndpoints: endpoints.length > 0,
    hasPartner: !!partner,
    partner,
    endpoints,
  };
};
